import { IRevenueScheduleSummary } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { FC, useCallback, useRef, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import {
  useDownloadLatestRevenueScheduleByContract,
  useGetActivePeriodLock,
  useGetRevenueScheduleSubscriptionItems,
  useUpdateRevenueScheduleOverrides,
} from '../../api/revenueService';
import { useConfirmModal } from '../../services/confirmModal';
import { logger } from '../../services/logger';
import { downloadBlobAsFile } from '../../utils/download';
import {
  KeyValueCallout,
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MPageLoader,
} from '../Monetize';
import { RevenueScheduleTable } from './RevenueScheduleTable';
import { useRevenueSchedulePeriodsInfinite } from './useRevenueSchedulePeriodsInfinite';

interface LatestScheduleSectionProps {
  schedule: IRevenueScheduleSummary;
  revenueContractId: string;
  currency: string;
}

export const LatestScheduleSection: FC<LatestScheduleSectionProps> = ({
  schedule,
  revenueContractId,
  currency,
}) => {
  const [isEditable, setIsEditable] = useState(false);
  const [isEnteringEditMode, setIsEnteringEditMode] = useState(false);
  // Held in a ref, not state: edits are only read at save time and cleared on
  // cancel — never rendered here. Using state would re-render this component on
  // every keystroke, handing the table fresh `periods`/column references and
  // remounting the focused input.
  const editedRevenuesRef = useRef<Record<string, number>>({});

  const contractStart = schedule.contractData?.term?.start ?? null;
  const contractEndInclusive =
    schedule.contractData?.term?.endInclusive ?? null;

  const {
    periods,
    loadedThrough,
    isInitialLoading,
    isFetchingMore,
    loadNextWindow,
    loadAll,
  } = useRevenueSchedulePeriodsInfinite({
    scheduleId: schedule.id,
    contractStart,
    contractEndInclusive,
  });

  const { data: subscriptionItems } = useGetRevenueScheduleSubscriptionItems(
    schedule.id,
  );
  const { data: activeLock } = useGetActivePeriodLock({
    meta: { showErrorToast: true },
  });

  const handleEditedRevenuesChange = useCallback(
    (edits: Record<string, number>) => {
      editedRevenuesRef.current = edits;
    },
    [],
  );

  const { mutateAsync: updateOverrides, isPending: isSavingOverrides } =
    useUpdateRevenueScheduleOverrides({ meta: { showErrorToast: true } });

  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();

  const handleSaveOverrides = () => {
    const periodOverrides = Object.entries(editedRevenuesRef.current).map(
      ([periodId, revenue]) => ({ periodId, revenue }),
    );
    if (periodOverrides.length === 0) {
      setIsEditable(false);
      return;
    }
    showConfirmModal({
      title: 'Save revenue schedule?',
      description:
        'Confirming will create a new version of this revenue schedule, which will be used to generate journal entries. You will not be able to automatically revert to a prior version.',
      yesButton: 'Confirm & Save',
      noButton: 'Cancel',
      yesBtnProps: { variant: 'primary' },
      noBtnProps: { variant: 'tertiary' },
      onYes: async () => {
        setModalLoading(true);
        try {
          await updateOverrides({ scheduleId: schedule.id, periodOverrides });
          setIsEditable(false);
          closeModal();
        } catch (error) {
          logger.error('Error saving revenue schedule overrides:', error);
        } finally {
          setModalLoading(false);
        }
      },
    });
  };

  const handleEnterEditMode = async () => {
    setIsEnteringEditMode(true);
    try {
      // Make sure every period in the contract range is loaded before the
      // user can start editing — otherwise totals and the TCV mismatch
      // indicator would be computed against a partial dataset.
      await loadAll();
      setIsEditable(true);
    } catch (error) {
      logger.error('Error loading all revenue periods for edit mode:', error);
    } finally {
      setIsEnteringEditMode(false);
    }
  };

  const totalContractValue = schedule.contractData?.amount ?? null;
  const contractStartLabel = contractStart ? toDateShort(contractStart) : null;
  const contractEndLabel = contractEndInclusive
    ? toDateShort(contractEndInclusive)
    : null;

  const { isPending: isDownloading, mutateAsync: downloadLatestSchedule } =
    useDownloadLatestRevenueScheduleByContract({
      meta: { showErrorToast: true },
    });

  const handleDownloadSchedule = async () => {
    try {
      const result = await downloadLatestSchedule({
        revenueContractId,
        fallbackId: schedule.references?.quoteId ?? schedule.id,
      });
      downloadBlobAsFile(result.data, result.filename, 'text/csv');
    } catch (error) {
      logger.error('Error downloading revenue schedule:', error);
    }
  };

  return (
    <MBox data-testid="revenue-contract-latest-schedule">
      <MFlex
        justifyContent="space-between"
        alignItems="flex-start"
        mb={4}
        gap={4}
      >
        <MFlex alignItems="flex-start" gap={6}>
          <KeyValueCallout
            testIdRoot="revenue-contract-tcv"
            type="currency"
            label="Total Contract Value"
            value={totalContractValue}
            currency={currency}
          />
          <KeyValueCallout
            testIdRoot="revenue-contract-start"
            type="text"
            label="Contract Start"
            value={contractStartLabel}
          />
          <KeyValueCallout
            testIdRoot="revenue-contract-end"
            type="text"
            label="Contract End"
            value={contractEndLabel}
          />
        </MFlex>
        <MFlex alignItems="center" gap={2}>
          <MCustomIconButton
            icon={MdOutlineFileDownload}
            aria-label="Download schedule"
            isDisabled={!revenueContractId}
            isLoading={isDownloading}
            onClick={handleDownloadSchedule}
            w="6"
            h="6"
            size="md"
          />
          {isEditable ? (
            <>
              <MButton
                variant="tertiary"
                onClick={() => {
                  editedRevenuesRef.current = {};
                  setIsEditable(false);
                }}
                isDisabled={isSavingOverrides}
              >
                Cancel
              </MButton>
              <MButton
                variant="primary"
                onClick={handleSaveOverrides}
                isLoading={isSavingOverrides}
              >
                Save
              </MButton>
            </>
          ) : (
            <MButton
              variant="secondary"
              onClick={handleEnterEditMode}
              isLoading={isEnteringEditMode}
            >
              Edit
            </MButton>
          )}
        </MFlex>
      </MFlex>

      {isInitialLoading ? (
        <MPageLoader height="auto" />
      ) : (
        <RevenueScheduleTable
          subscriptionItems={subscriptionItems ?? []}
          periods={periods}
          contractStart={contractStart}
          contractEndInclusive={contractEndInclusive}
          loadedThrough={loadedThrough}
          isEditable={isEditable}
          contractAmount={totalContractValue}
          activeLock={activeLock}
          isLoadingMore={isFetchingMore}
          onLoadMore={loadNextWindow}
          onChangeEditedRevenues={handleEditedRevenuesChange}
        />
      )}
    </MBox>
  );
};
